/**
 * What the assistant is told, and what it is allowed to see.
 *
 * The console assistant answers from a context block the server assembles from
 * the caller's own fleet. It has no tools and cannot fetch anything: whatever
 * is not in the block does not exist as far as it is concerned. That is the
 * tenant boundary — not a prompt instruction, but the absence of any path to
 * another tenant's data.
 */

import type { Fact } from "@carrier/ocs-client";

/**
 * The AI Act, Article 50(1), in force 2 August 2026: a person interacting with
 * an AI system must be told so, unless it is obvious. The UI labels the
 * assistant, and the model is told not to claim otherwise.
 */
export const CARRIER_ASSISTANT_SYSTEM = `You are the Carrier assistant. Carrier is an eSIM and MVNO platform; the people you talk to run fleets of SIMs and care about cost, coverage, and subscribers that stop working.

You are given a CONTEXT block containing figures already measured from this operator's own fleet. Answer from it.

Rules:
1. Use only figures that appear in the CONTEXT block. Never estimate, extrapolate, or invent a number. If the answer needs a figure you were not given, say which one you would need.
2. "I don't have that in front of me" is a correct answer. Prefer it to a plausible guess. These numbers drive billing decisions.
3. Be concrete and short. Lead with the answer. No preamble, no restating the question, no summary at the end.
4. You see this operator's fleet only. You have no access to any other operator's data, and you must not speculate about other operators.
5. Subscribers appear as labels like SUB-1. Use the label; the system substitutes the real identifier for the reader.
6. You are an AI assistant. If asked, say so plainly.
7. Plain words. No marketing language, no "comprehensive", no exclamation marks.`;

/** One measured section of the context block. */
export interface ContextSection {
  title: string;
  facts: Fact[];
  /** Optional prose the server computed, e.g. a deterministic verdict. */
  note?: string;
}

/**
 * Render the context block.
 *
 * Facts arrive already formatted for display, so the model never does
 * arithmetic and never chooses a unit. It reads "2.14 GB", not 2297... bytes.
 */
export function renderContext(sections: ContextSection[]): string {
  if (sections.length === 0) {
    return "CONTEXT\n(No fleet figures were loaded for this question.)";
  }

  const lines: string[] = ["CONTEXT"];
  for (const section of sections) {
    lines.push(`\n## ${section.title}`);
    if (section.facts.length === 0) lines.push("(no figures)");
    for (const f of section.facts) {
      lines.push(`- ${f.label}: ${f.display}`);
    }
    if (section.note) lines.push(`Note: ${section.note}`);
  }
  return lines.join("\n");
}

/**
 * The system prompt for a panel's mini chat.
 *
 * Same rules, narrower remit. A mini chat that starts answering questions about
 * the whole fleet is a worse product than one that says "that is a question for
 * the assistant" — the user asked it next to one table for a reason.
 */
export function panelSystem(panelTitle: string): string {
  return `${CARRIER_ASSISTANT_SYSTEM}

You are answering next to the "${panelTitle}" panel. Stay with what that panel shows. If the question needs data from elsewhere in Carrier, say so in one line and suggest the full assistant instead of guessing.`;
}
